import React, { useState, useEffect } from "react";
import { MdArrowDropUp, MdArrowDropDown, MdOutlineCalendarToday } from "react-icons/md";
import Card from "components/card";
import LineChart from "components/charts/LineChart";
import AxiosInstance from "../../../../axiosConfig";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const MonthlyActivity = ({ botId }) => {
  const [chartData, setChartData] = useState([]);
  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: "line",
      toolbar: { show: false },
    },
    xaxis: {
      categories: [],
    },
  });
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [isLoading, setIsLoading] = useState(false);
  const [percentageIncrease, setPercentageIncrease] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const year = selectedDate.year();
        const month = selectedDate.month() + 1; // dayjs months are 0-based
        const formattedDate = `${year}-${month.toString().padStart(2, "0")}-01`;

        const response = await AxiosInstance.post("/messaging-activity-trends", {
          duration_in: "months",
          stats_date_from: formattedDate,
          array_expected: true,
        });

        const data = response.data;
        // Convert object { "YYYY-MM": value } to array [{ month: "YYYY-MM", texts_sent: value }]
        const dataArray = Object.entries(data).map(([month, texts_sent]) => ({
          month,
          texts_sent,
        }));

        // Sort by date to ensure chronological order
        dataArray.sort((a, b) => dayjs(a.month).isBefore(dayjs(b.month)) ? -1 : 1);

        const categories = dataArray.map((item) => item.month);
        const seriesData = dataArray.map((item) => item.texts_sent);

        setChartOptions((prevOptions) => ({
          ...prevOptions,
          xaxis: { categories },
        }));
        setChartData([{ name: "Texts Sent", data: seriesData }]);

        // Calculate percentage increase
        const totalMonths = seriesData.length;
        if (totalMonths > 1) {
          const currentMonthValue = seriesData[totalMonths - 1];
          const previousMonthsAverage =
            seriesData.slice(0, totalMonths - 1).reduce((sum, value) => sum + value, 0) /
            (totalMonths - 1);

          if (previousMonthsAverage === 0) {
            setPercentageIncrease(currentMonthValue > 0 ? 100 : 0);
          } else {
            const percentage = ((currentMonthValue - previousMonthsAverage) / previousMonthsAverage) * 100;
            setPercentageIncrease(percentage.toFixed(1));
          }
        } else {
          setPercentageIncrease(0);
        }
      } catch (error) {
        console.error("Error fetching data: ", error);
        setChartData([]);
        setChartOptions((prevOptions) => ({
          ...prevOptions,
          xaxis: { categories: [] },
        }));
        setPercentageIncrease(0);
      } finally {
        setIsLoading(false);
      }
    };

    if (botId) {
      fetchData();
    }
  }, [selectedDate, botId]);

  const handleDateChange = (newValue) => {
    setSelectedDate(newValue);
    setShowDatePicker(false);
  };

  const formattedPercentageIncrease =
    percentageIncrease !== null && percentageIncrease !== undefined
      ? percentageIncrease
      : "0.0";

  return (
    <Card extra="!p-[20px] text-center">
      <div className="flex justify-between">
        <div className="relative">
          <div
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="linear mt-1 flex items-center justify-center gap-2 rounded-lg bg-lightPrimary p-2 text-gray-600 transition duration-200 hover:cursor-pointer hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:hover:opacity-90 dark:active:opacity-80"
          >
            {selectedDate.format("MMMM D, YYYY")}
            <MdOutlineCalendarToday className="text-gray-600" />
          </div>
          {showDatePicker && (
            <div className="absolute z-10 mt-1">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                  minDate={dayjs().subtract(5, 'year')} // 5 years back from today (July 20, 2020)
                  maxDate={dayjs()} // Today (July 20, 2025)
                  open={showDatePicker}
                  onClose={() => setShowDatePicker(false)}
                  views={['year', 'month', 'day']}
                  format="MMMM D, YYYY"
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          '&:hover fieldset': {
                            borderColor: '#d1d5db',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                          },
                        },
                      },
                    },
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          )}
        </div>
        <div>
          <h4 className="text-lg font-bold text-navy-700 dark:text-white" style={{ color: '#111827' }}>
            Monthly Activity
          </h4>
          <p className="text-sm font-medium leading-4 text-gray-600">
            Number of texts sent per month
          </p>
        </div>
        <div className="flex flex-row items-center justify-center">
          {percentageIncrease !== null && percentageIncrease !== 0 ? (
            percentageIncrease > 0 ? (
              <MdArrowDropUp className="font-medium text-green-500" />
            ) : (
              <MdArrowDropDown className="font-medium text-red-500" />
            )
          ) : null}
          <p
            className={`text-sm font-bold ${
              percentageIncrease > 0
                ? "text-green-500"
                : percentageIncrease < 0
                ? "text-red-500"
                : "text-gray-500"
            }`}
          >
            {isLoading
              ? "Loading..."
              : `${formattedPercentageIncrease}% ${
                  percentageIncrease > 0
                    ? "increase"
                    : percentageIncrease < 0
                    ? "decrease"
                    : "no change"
                }`}
          </p>
        </div>
      </div>

      <div className="flex h-full w-full flex-row justify-between sm:flex-wrap lg:flex-nowrap 2xl:overflow-hidden">
        <div className="flex flex-col">
          <div className="flex flex-col items-start"></div>
        </div>
        <div className="h-full w-full">
          <LineChart options={chartOptions} series={chartData} />
        </div>
      </div>
    </Card>
  );
};

export default MonthlyActivity;